import React from 'react';

import { cn } from '@/lib/utils';

import { gradientDecodoBorderStyling } from '@/styles/gradient';
import { Colors } from '@/styles/types';
import { passDataTrackerRef } from '../Button/utils';

export const sizesOptions = ['s', 'm', 'l'] as const;
const sizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-2 py-[2px] text-label-s-medium',
  m: 'px-2 py-1 text-label-s-medium',
  l: 'px-2 py-1 text-label-medium',
};

const gradientExtraSizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-[9px] py-[3px]',
  m: 'px-[9px] pt-[4px] pb-[3px]',
  l: 'px-[9px] py-[5px]',
};
export const variantOptions = ['filled', 'outlined'] as const;
export const colorsOptions = [
  'gray-900',
  'gray-200',
  'gray-300',
  'success-500',
  'success-100',
  'success-600',
  'tertiary-500',
  'tertiary-100',
  'tertiary-600',
  'brand-500',
  'brand-100',
  'secondary-500',
  'secondary-100',
  'secondary-600',
  'error-500',
  'error-100',
  'warning-500',
  'warning-100',
  'warning-600',
  'gradient',
  'light-gradient',
] satisfies Colors[];

export interface BadgeProps {
  variant?: (typeof variantOptions)[number];
  size?: keyof typeof sizes;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  colors?: (typeof colorsOptions)[number];
  classNames?: {
    root?: string;
  };
}

interface BadgePropsWithHtml
  extends BadgeProps,
    React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export default function Badge({
  children,
  classNames,
  colors = 'success-500',
  variant = 'filled',
  size = 'm',
  leftSection,
  rightSection,
  ...rest
}: BadgePropsWithHtml) {
  const colorClasses = {
    'gray-900': {
      filled:
        'bg-gray-900 group-hover:bg-gray-900 text-base-white group-hover:text-base-black',
      outlined:
        'bg-transparent border-gray-900 text-gray-900 group-hover:bg-gray-900 hover:bg-gray-900 group-hover:text-base-white hover:text-base-white',
    },
    'gray-200': {
      filled:
        'bg-gray-200 group-hover:bg-gray-200 text-base-black group-hover:text-base-white',
      outlined:
        'bg-transparent border-gray-200 text-gray-200 group-hover:bg-gray-20 hover:bg-gray-200 group-hover:text-base-black hover:text-base-black',
    },
    'gray-300': {
      filled:
        'bg-gray-300 group-hover:bg-gray-300 text-base-black group-hover:text-base-white',
      outlined:
        'bg-transparent border-gray-300 text-base-black group-hover:bg-base-black hover:bg-base-black hover:text-base-white group-hover:text-base-white',
    },
    'success-500': {
      filled:
        'bg-success-500 group-hover:bg-success-500 text-base-white group-hover:text-base-black',
      outlined:
        'bg-transparent border-success-500 text-success-500 group-hover:bg-success-500 group-hover:text-base-white',
    },
    'success-100': {
      filled:
        'bg-success-100 dark:bg-success-900 group-hover:bg-success-100 dark:group-hover:bg-success-900 text-success-700 dark:text-success-300 dark:group-hover:text-base-white group-hover:text-base-black',
      outlined:
        'bg-transparent border-success-100 text-success-100 group-hover:bg-success-100 group-hover:text-base-black',
    },
    'success-600': {
      filled:
        'bg-success-600 group-hover:bg-success-600 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-success-600  text-success-600  group-hover:bg-success-600 group-hover:text-base-white',
    },
    'tertiary-500': {
      filled:
        'bg-tertiary-500 group-hover:bg-tertiary-500 text-base-white group-hover:text-base-black',
      outlined:
        'bg-transparent border-tertiary-500 text-tertiary-500 group-hover:bg-tertiary-500 group-hover:text-base-white',
    },
    'tertiary-100': {
      filled:
        'bg-tertiary-100 group-hover:bg-tertiary-100 group-hover:text-base-black text-tertiary-700',
      outlined:
        'bg-transparent border-tertiary-100 text-tertiary-100 group-hover:bg-tertiary-100 group-hover:text-base-black',
    },
    'tertiary-600': {
      filled:
        'bg-tertiary-600 group-hover:bg-tertiary-600 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-tertiary-600 text-tertiary-600 group-hover:bg-tertiary-600 group-hover:text-base-white',
    },
    'brand-500': {
      filled: cn(
        'bg-brand-500 hover:bg-brand-500 hover:text-base-black text-base-white',
        'dark:bg-brand-900 dark:border-brand-900 dark:text-brand-300'
      ),
      outlined:
        'bg-transparent border-brand-500 text-brand-500 group-hover:bg-brand-500 hover:bg-brand-500 group-hover:text-base-white hover:text-base-white',
    },

    'brand-100': {
      filled:
        'bg-brand-100 group-hover:brand-100 text-brand-700 group-hover:text-base-black dark:bg-brand-900 dark:text-brand-300',
      outlined:
        'bg-transparent border-gray-900 text-gray-900 group-hover:bg-gray-900 group-hover:text-base-white hover:bg-gray-900 hover:text-base-white',
    },
    'secondary-500': {
      filled:
        'bg-secondary-500 group-hover:bg-secondary-500 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-secondary-500 text-secondary-500 group-hover:bg-secondary-500 group-hover:text-base-white',
    },
    'secondary-100': {
      filled:
        'bg-secondary-100 group-hover:bg-secondary-100 group-hover:text-base-black text-secondary-700',
      outlined:
        'bg-transparent border-secondary-100 text-secondary-100 group-hover:bg-secondary-100 group-hover:text-base-black',
    },
    'secondary-600': {
      filled:
        'bg-secondary-600 group-hover:bg-secondary-600 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-secondary-600 text-secondary-600 group-hover:bg-secondary-600 group-hover:text-base-white',
    },
    'error-500': {
      filled:
        'bg-error-500 group-hover:bg-error-500 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-error-500 text-error-500 group-hover:bg-error-500 group-hover:text-base-white',
    },
    'error-100': {
      filled:
        'bg-error-100 group-hover:bg-error-100 group-hover:text-base-black text-error-700',
      outlined:
        'bg-transparent border-error-100 text-error-100 group-hover:bg-error-100 group-hover:text-base-black',
    },
    'warning-500': {
      filled:
        'bg-warning-500 group-hover:bg-warning-500 group-hover:text-base-black text-base-white',
      outlined:
        'bg-transparent border-warning-500 text-warning-500 group-hover:bg-warning-500 group-hover:text-base-white',
    },
    'warning-100': {
      filled:
        'bg-warning-100 group-hover:bg-warning-100 text-warning-700 group-hover:text-base-black',
      outlined:
        'bg-transparent border-warning-100 text-warning-100 group-hover:bg-warning-100 group-hover:text-base-black',
    },
    'warning-600': {
      filled:
        'bg-warning-600 group-hover:bg-warning-600 group-hover:text-base-black',
      outlined:
        'bg-transparent border-warning-600 text-warning-600 group-hover:bg-warning-600 group-hover:text-base-white',
    },
    gradient: {
      filled:
        'text-base-white bg-gradient-secondary-1 dark:text-base-white group-hover:text-base-black',
      outlined: 'text-base-black',
    },
    'light-gradient': {
      filled:
        'text-base-black bg-gradient-secondary-5 group-hover:text-base-white',
      outlined: 'text-base-white',
    },
  } as const;
  const specificColorClasses = colorClasses[colors]?.[variant] || '';

  return (
    <div
      {...passDataTrackerRef<HTMLDivElement>()}
      className={`${cn(
        `flex w-fit items-center justify-center gap-1.5 whitespace-nowrap align-middle font-serif transition-colors duration-300`,
        'uppercase',
        specificColorClasses,
        sizes[size],
        colors === 'gradient' && variant === 'filled'
          ? `${gradientExtraSizes[size]}`
          : `border-${colors}`,
        colors === 'gradient' && variant === 'outlined'
          ? `${gradientDecodoBorderStyling({
              borderWidth: 'p-px',
              color: 'gradient-main',
              borderRadius: 'rounded-3xl',
            })} ${
              gradientExtraSizes[size]
            } group-hover:bg-gradient-main relative !rounded-3xl`
          : `rounded-3xl`,
        variant === 'outlined' &&
          colors !== 'gradient' &&
          'border border-solid',
        colors === 'success-500' && variant === 'filled' && 'text-base-black',
        classNames?.root
      )}`}
      data-tracker-component='badge'
      {...rest}
    >
      {leftSection}
      {children}
      {rightSection}
    </div>
  );
}
